/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/*!
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@                                         @@@@
 *!   @@@   THIS FILE WAS AUTOGENERATED BY TOOL   @@@@
 *!   @@@   CHANGES MUST TARGET SCRIPTS/SWAGGER   @@@@
 *!   @@@                                         @@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!
 */

import {
  _buildQueryString,
  _emptyParams,
  _encode,
  _getBasePath,
  _getRawBasePath,
} from './urls-utils'

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/instances
 * Operation: `get-instance-configurations`
 * Get instance configurations.
 * @param {boolean} params.showDeleted If true, instance configurations marked for deletions are also returned. Otherwise, only instance configurations not marked for deletion are returned.
 * @param {boolean} params.showMaxZones If true, will populate the max_zones field of the instance configurations.
 * @param {boolean} params.includeVersions If true, will return all existing versions of each instance configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 A InstanceConfiguration. See {@link Array<InstanceConfiguration>}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getInstanceConfigurationsUrl(params: GetInstanceConfigurationsUrlParams): string {
  const queryString = _buildQueryString({
    show_deleted: params.showDeleted,
    show_max_zones: params.showMaxZones === undefined ? false : params.showMaxZones,
    include_versions: params.includeVersions === undefined ? false : params.includeVersions,
  })
  const path = _encode`/${params.regionId}/platform/configuration/instances`
  return `${_getBasePath()}${path}${queryString}`
}

export interface GetInstanceConfigurationsUrlParams {
  showDeleted?: boolean | null
  showMaxZones?: boolean | null
  includeVersions?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/instances
 * Operation: `create-instance-configuration`
 * Create instance configuration and return the auto-generated ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link InstanceConfiguration}
 * @see 201 Instance Configuration added with the auto generated ID returned. See {@link VersionedIdResponse}
 * @see 400 cluster_type in the InstanceConfiguration model is invalid (code: 'configuration.invalid_cluster_type') or the discrete_sizes in the InstanceConfiguration model is invalid (code: 'configuration.invalid_discrete_sizes') or the metadata in the InstanceConfiguration model has empty keys or values (code: 'configuration.bad_meta_data') . See {@link BasicFailedReply}
 * @see 403 system_owned or deleted_on cannot be set externally (code: 'configuration.system_owned') . See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createInstanceConfigurationUrl(
  params: CreateInstanceConfigurationUrlParams,
): string {
  const path = _encode`/${params.regionId}/platform/configuration/instances`
  return `${_getBasePath()}${path}`
}

export interface CreateInstanceConfigurationUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/instances/{id}
 * Operation: `get-instance-configuration`
 * Get instance configuration by id.
 * @param {string} params.id ID of the instance configuration.
 * @param {integer} params.configVersion Optionally retrieve the specified config version of the IC (otherwise retrieves the latest/only version).
 * @param {boolean} params.showDeleted If true, if the instance configuration has been marked for deletion it is still returned. Otherwise, instance configurations marked for deletion generate a 404.
 * @param {boolean} params.showMaxZones If true, will populate the max_zones field of the instance configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An InstanceConfiguration. See {@link InstanceConfiguration}
 * @see 404 Instance configuration specified by {id} cannot be found (code: 'configuration.instance_configuration_not_found'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getInstanceConfigurationUrl(params: GetInstanceConfigurationUrlParams): string {
  const queryString = _buildQueryString({
    config_version: params.configVersion,
    show_deleted: params.showDeleted,
    show_max_zones: params.showMaxZones === undefined ? false : params.showMaxZones,
  })
  const path = _encode`/${params.regionId}/platform/configuration/instances/${params.id}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface GetInstanceConfigurationUrlParams {
  id: string
  configVersion?: number | null
  showDeleted?: boolean | null
  showMaxZones?: boolean | null
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/instances/{id}
 * Operation: `set-instance-configuration`
 * Creates or updates an instance configuration.
 * @param {string} params.id ID of the instance configuration.
 * @param {integer} params.version This is a database-level field, not related to the application-level 'config_version', except as described in the following docs. If specified, checks for conflicts against 'x-cloud-resource-version' from the GET request (where the 'config_version' in the GET should match the the 'configuration_version' in the PUT body if the IC is configuration controlled).
 * @param {boolean} params.configControl If true, then an update that changes immutable fields will create a new version of the IC (leaving the previous ones accessible via 'config_version'). Otherwise such updates will fail.
 * @param {boolean} params.testConfig If true, The IC is added as a test version (set 'instance_config_version': -1 in the deployment request to reference), leaving the existing IC alone. Will error if the IC doesn't already exist.
 * @param {boolean} params.strictMode If true, then allocator_filter and storage_multiplier cannot be changed in an existing IC. Defaults to false unless config_control is true (in which case a new version is created).
 * @param {boolean} params.createOnly If true, will fail if an instance configuration already exists at the given id.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link InstanceConfiguration}
 * @see 200 The instance configuration was updated successfully. See {@link VersionedIdResponse}
 * @see 201 The instance configuration was created successfully. See {@link VersionedIdResponse}
 * @see 400 cluster_type in the InstanceConfiguration model is invalid (code: 'configuration.invalid_cluster_type') or the ZooKeeper operation failed due to bad version, etc. (code: 'configuration.instance_configuration_update_failed') or the id in the InstanceConfiguration model is reserved/prohibited (code: 'configuration.configuration_id_reserved') or the discrete_sizes in the InstanceConfiguration model is invalid (code: 'configuration.invalid_discrete_sizes') or the metadata in the InstanceConfiguration model has empty keys or values (code: 'configuration.bad_meta_data') . See {@link BasicFailedReply}
 * @see 403 system_owned or deleted_on cannot be set externally (code: 'configuration.system_owned') . See {@link BasicFailedReply}
 * @see 404 Instance configuration specified by {id} cannot be found or the operation failed (code: 'configuration.instance_configuration_not_found'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setInstanceConfigurationUrl(params: SetInstanceConfigurationUrlParams): string {
  const queryString = _buildQueryString({
    version: params.version,
    config_control: params.configControl,
    test_config: params.testConfig,
    strict_mode: params.strictMode,
    create_only: params.createOnly,
  })
  const path = _encode`/${params.regionId}/platform/configuration/instances/${params.id}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface SetInstanceConfigurationUrlParams {
  id: string
  version?: number | null
  configControl?: boolean | null
  testConfig?: boolean | null
  strictMode?: boolean | null
  createOnly?: boolean | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/instances/{id}
 * Operation: `delete-instance-configuration`
 * Delete the instance configuration with the given id.
 * @param {string} params.id ID of the instance configuration.
 * @param {integer} params.version This is a database-level field, not related to the application-level 'config_version', except as described in the following docs. If specified, checks for conflicts against 'x-cloud-resource-version' from the GET request (the GET's 'config_version' should be left blank if the IC is configuration controlled, ie to get the latest configuration).
 * @param {boolean} params.onlyTestVersion If true, the testing version (version -1) will be permanently deleted but the latest IC version will remain untouched. Defaults to false.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Instance configuration specified by {id} was deleted
 * @see 403 Instance configuration specified by {id} is system owned (code: 'configuration.system_owned'). See {@link BasicFailedReply}
 * @see 404 Instance configuration specified by {id} cannot be found or the operation failed (code: 'configuration.instance_configuration_not_found'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteInstanceConfigurationUrl(
  params: DeleteInstanceConfigurationUrlParams,
): string {
  const queryString = _buildQueryString({
    version: params.version,
    only_test_version: params.onlyTestVersion,
  })
  const path = _encode`/${params.regionId}/platform/configuration/instances/${params.id}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface DeleteInstanceConfigurationUrlParams {
  id: string
  version?: number | null
  onlyTestVersion?: boolean | null
  regionId: string
}
